/**
 * **节点 id 生成器** —— 把原来藏在闭包里的计数器显式化。
 *
 * ## 为什么单独拿出来
 *
 * `Playground.vue` 原来是这样：
 *
 * ```ts
 * let nodeSeq = 0                       // ← 一个「隐藏的」可变状态
 * function nextNodeId(type: string): string {
 *   nodeSeq += 1
 *   return `${type}_${nodeSeq}_${Date.now().toString(36)}`
 * }
 * ```
 *
 * 问题不在「用了闭包」，而在**依赖是隐式的**：
 * `nextNodeId` 看起来是个纯字符串工具，实际带着一份跨调用存活的状态。
 * 这和第 30 轮修的 `makeEdge` 读闭包里的 `socketTypeOf` 是同一类毛病 ——
 * 都是「读的时候看不出它依赖什么」。
 *
 * 现在改成工厂：计数器被关在返回的闭包里，**用 `useNodeIdSeq()` 创建**，
 * 依赖一眼可见，也能独立测试（见 `scripts/verify-layers.ts`）。
 *
 * ## 和 `useXxx` 的区别
 *
 * 它**不碰 Vue 响应式**，也不是宿主状态 —— 只是一个有内部计数器的生成器。
 * 之所以也叫 `use`，是为了和这个目录里其它工厂读起来一致。
 */

/** 形如 `文本组件_3_kx9f2a` —— 前缀 + 序号 + 36 进制时间戳 */
export interface NodeIdSeq {
  next(type: string): string
}

/**
 * 创建一个 id 生成器。
 *
 * @param now 取时间戳的函数（测试里可注入固定值）
 */
export function useNodeIdSeq(now: () => number = Date.now): NodeIdSeq {
  let seq = 0
  return {
    next(type: string): string {
      seq += 1
      const safe = type.replace(/[^a-z0-9]+/gi, '_')
      return `${safe}_${seq}_${now().toString(36)}`
    },
  }
}
